import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { MAX_TYPING_INDICATOR_MS } from '@/config/constants';
import { cn } from '@/lib/utils';
import { ErrorPage } from '@/newComponents/ErrorPage';
import { Header } from '@/newComponents/Header/Header';
import { LoadingPage } from '@/newComponents/LoadingPage';
import { WelcomeFields } from '@/newComponents/WelcomeFields/WelcomeFields';
import { isLeftAlignedSelector, isTopAlignedSelector, showCloseIconSelector, widgetPositionState, } from '@/state/config';
import { composeSelector, composeState, errorSessionSelector, isMobileState, chatEventCountSelector, showWelcomeFormSelector, isWidgetExpandedState, } from '@/state/session';
import { isLoadingSelector } from '@/state/socket';
import { Chat } from './Chat';
const baseStyles = 'chatContainer bg-chat-background ';
const layoutStyles = {
    base: 'shadow-sm max-h-screen border border-border',
    fullScreen: 'h-full w-full overflow-hidden',
    mobile: 'bottom-0 w-full rounded-none bg-[var(--text-03)] opacity-100 overflow-hidden',
};
const positionStyles = {
    livechatBase: 'fixed h-[var(--moveo-chat-height)] w-[var(--moveo-chat-width)] overflow-hidden rounded-[var(--moveo-new-border-radius-base)] transition-[width] duration-100 ease-in-out duration-300',
    livechatWithLauncher: 'max-h-[calc(100vh-80px)]',
    livechatNoLauncher: 'max-h-[calc(100vh-16px)]',
    livechatBottom: 'right-0 bottom-16 animate-in fade-in slide-in-from-bottom-4 fade-out slide-out-to-bottom-4',
    livechatBottomNoLauncher: 'right-0 bottom-2 animate-in fade-in slide-in-from-bottom-4 fade-out slide-out-to-bottom-4',
    livechatTop: 'right-0 top-0 animate-in fade-in slide-in-from-top-4 fade-out slide-out-to-top-4',
    expanded: 'w-[var(--moveo-expanded-chat-width)]',
    leftAligned: 'left-0 right-auto',
};
export const ChatContainer = () => {
    const timerRef = useRef();
    const isLeft = useRecoilValue(isLeftAlignedSelector);
    const isTop = useRecoilValue(isTopAlignedSelector);
    const setCompose = useSetRecoilState(composeState);
    const compose = useRecoilValue(composeSelector);
    const { isLivechat, isPopup, isEmbed } = useRecoilValue(widgetPositionState);
    const isMobile = useRecoilValue(isMobileState);
    const error = useRecoilValue(errorSessionSelector);
    const chatEventsCount = useRecoilValue(chatEventCountSelector);
    const showWelcomeForm = useRecoilValue(showWelcomeFormSelector);
    const isLoading = useRecoilValue(isLoadingSelector);
    const isWidgetExpanded = useRecoilValue(isWidgetExpandedState);
    const showCloseIcon = useRecoilValue(showCloseIconSelector);
    // Disable compose after 10 seconds of innactivity(MAX_TYPING_INDICATOR_MS)
    useEffect(() => {
        if (!!compose && chatEventsCount) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = window.setTimeout(() => {
                setCompose(null);
            }, MAX_TYPING_INDICATOR_MS);
        }
        else {
            clearTimeout(timerRef.current);
        }
    }, [chatEventsCount, compose, setCompose]);
    const getPositionStyles = () => {
        if (isLivechat && !isMobile) {
            const hasLauncher = showCloseIcon;
            return cn(positionStyles.livechatBase, hasLauncher
                ? positionStyles.livechatWithLauncher
                : positionStyles.livechatNoLauncher, isTop
                ? positionStyles.livechatTop
                : hasLauncher
                    ? positionStyles.livechatBottom
                    : positionStyles.livechatBottomNoLauncher, isWidgetExpanded && positionStyles.expanded, isLeft && positionStyles.leftAligned);
        }
        if (isPopup) {
            return cn(layoutStyles.fullScreen, isMobile && 'rounded-none');
        }
        if (isEmbed) {
            return layoutStyles.fullScreen;
        }
        return '';
    };
    return (_jsx(_Fragment, { children: _jsxs("div", { className: cn(baseStyles, layoutStyles.base, getPositionStyles(), {
                [layoutStyles.mobile]: isMobile && isLivechat,
            }), children: [_jsx(Header, {}), error && _jsx(ErrorPage, {}), !error && !isLoading && showWelcomeForm && _jsx(WelcomeFields, {}), !error && !isLoading && !showWelcomeForm && _jsx(Chat, {}), !error && isLoading && _jsx(LoadingPage, {})] }) }));
};
